import React, { Component, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import {
  Activity,
  Archive,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Database,
  Download,
  Eye,
  FileArchive,
  FileSearch,
  FileText,
  FolderOpen,
  LayoutDashboard,
  Lock,
  LogOut,
  Mail,
  Search,
  ShieldCheck,
  RefreshCw,
  UploadCloud,
  X,
} from "lucide-react";
import "./styles.css";
import { PlatformApp } from "./platform";

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "upload", label: "Upload", icon: UploadCloud },
  { id: "studies", label: "Studies", icon: Database },
];

function formatDate(value) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function useQuery() {
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search));

  useEffect(() => {
    const update = () => setQuery(new URLSearchParams(window.location.search));
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const updateQuery = (values) => {
    const next = new URLSearchParams(window.location.search);
    Object.entries(values).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    window.history.pushState(null, "", `${window.location.pathname}?${next.toString()}`);
    setQuery(next);
  };

  return [query, updateQuery];
}

function LoginPage() {
  const params = new URLSearchParams(window.location.search);
  const [showPassword,setShowPassword]=useState(false);

  return (
    <main className="login-page">
      <section className="login-artwork" aria-label="Dectrocel Study Portal">
        <div className="login-introduction">
          <img className="login-brand-logo" src="/dectrocel-brand.jpeg" alt="Dectrocel" />
          <h1>Study Portal</h1>
          <h2>Secure. Anonymized. Collaborative.</h2>
          <p>A unified platform for managing and accessing medical studies across leading healthcare institutions.</p>
          <div className="institution-grid" aria-label="Partner healthcare institutions">
            {[
              ["Medanta – The Medicity","/hospital-medanta.png"],
              ["King George's Medical University","/hospital-kgmu.png"],
              ["Dr. Ram Manohar Lohia Institute of Medical Sciences","/hospital-rml.png"],
              ["Postgraduate Institute of Medical Education and Research","/hospital-pgimer.png"],
              ["Sanjay Gandhi Postgraduate Institute of Medical Sciences","/hospital-sgpgims.png"],
              ["Yashoda Hospitals Hyderabad","/hospital-yashoda.jpg"],
            ].map(([name,src])=><div className="institution-card" key={name}><img src={src} alt={name}/></div>)}
          </div>
        </div>
      </section>

      <div className="login-credentials">
      <section className="login-panel" aria-label="Login">
        <div className="login-heading"><h2>Welcome Back</h2><p>Sign in to continue to your account</p></div>
        {params.get("error") ? <div className="alert error">{params.get("error")}</div> : null}
        <form className="auth-form" method="post" action="/login">
          <label>
            Email Address
            <span className="login-input"><Mail size={20}/><input type="email" name="username" autoComplete="username" placeholder="Enter your email" required /></span>
          </label>
          <label>
            Password
            <span className="login-input"><Lock size={20}/><input type={showPassword?"text":"password"} name="password" autoComplete="current-password" placeholder="Enter your password" required /><button type="button" aria-label={showPassword?"Hide password":"Show password"} onClick={()=>setShowPassword(value=>!value)}><Eye size={21}/></button></span>
          </label>
          <button className="primary-button" type="submit">
            Sign In
          </button>
        </form>
        <div className="login-security"><Lock size={15}/>Your data is secure with us.</div>
      </section>
      </div>
      <footer className="login-footer">© 2026 Dectrocel. All rights reserved.</footer>
    </main>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <article className={`stat-card ${tone}`}>
      <div className="stat-icon">
        <Icon size={20} />
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
      <div className="stat-spark" aria-hidden="true" />
    </article>
  );
}

function formatBytes(value = 0) {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function ImportStatusPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadStatus = () => {
    setLoading(true);
    fetch("/api/import-status")
      .then((response) => {
        const contentType = response.headers.get("content-type") || "";
        if (response.status === 401 || !contentType.includes("application/json")) {
          throw new Error("Session expired. Please refresh and log in again.");
        }
        if (!response.ok) {
          throw new Error("Unable to load import status.");
        }
        return response.json();
      })
      .then((payload) => {
        setStatus(payload);
        setError("");
      })
      .catch((error) => setError(error.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStatus();
    const timer = window.setInterval(loadStatus, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const items = status?.items || [];
  const processingCount = items.filter((item) => item.status === "processing").length;
  const queuedCount = items.filter((item) => item.status === "queued" || item.status === "waiting").length;

  return (
    <section className="surface-panel import-status-panel">
      <div className="section-head">
        <div>
          <p className="eyebrow">Drive import</p>
          <h2>Processing status</h2>
        </div>
        <button className="ghost-button compact" type="button" onClick={loadStatus}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>
      {loading && !status ? <div className="metadata-state">Loading import status...</div> : null}
      {error ? <div className="alert error">{error}</div> : null}
      {status && !status.enabled ? <div className="alert error">Local Drive import is not enabled.</div> : null}
      {status ? (
        <div className="import-status-summary">
          <span>{processingCount} processing</span>
          <span>{queuedCount} queued or waiting</span>
          <span>{items.length} tracked</span>
        </div>
      ) : null}
      {status && items.length === 0 ? <EmptyState label="No studies waiting in Drive folders." /> : null}
      {items.length ? (
        <div className="table-shell import-status-table">
          <table>
            <thead>
              <tr>
                <th>Folder</th>
                <th>Study</th>
                <th>Status</th>
                <th>Files</th>
                <th>Size</th>
                <th>Updated</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <td>{item.source}</td>
                  <td className="filename-cell">{item.name}</td>
                  <td>
                    <span className={`status-chip ${item.status}`}>{item.status.replace(/_/g, " ")}</span>
                  </td>
                  <td>{item.fileCount ?? "-"}</td>
                  <td>{item.sizeBytes ? formatBytes(item.sizeBytes) : "-"}</td>
                  <td>{item.updatedAt ? formatDate(item.updatedAt) : "-"}</td>
                  <td className="status-message">{item.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function DashboardHome({ records, stats, setTab }) {
  return (
    <div className="view-stack">
      <section className="stats-grid">
        <StatCard icon={Archive} label="Total studies" value={stats.totalStudies} tone="teal" />
        <StatCard icon={FileArchive} label="DICOM files" value={stats.totalDicomFiles} tone="indigo" />
        <StatCard icon={Activity} label="CT studies" value={stats.ctStudies} tone="green" />
        <StatCard icon={FileText} label="Reports" value={stats.reports} tone="amber" />
      </section>

      <section className="surface-panel">
        <div className="section-head">
          <div>
            <p className="eyebrow">Recent activity</p>
            <h2>Latest uploads</h2>
          </div>
          <button className="ghost-button" type="button" onClick={() => setTab("studies")}>
            View all
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="activity-list">
          {records.slice(0, 5).map((record) => (
            <div className="activity-row" key={record.id}>
              <div className="record-badge">{record.recordType === "CT Study" ? "CT" : "XR"}</div>
              <div>
                <strong>{record.originalFilename}</strong>
                <span>{record.anonymizedPatientId}</span>
              </div>
              <span>{record.fileCount} item{record.fileCount === 1 ? "" : "s"}</span>
              <time>{formatDate(record.updatedAt || record.createdAt)}</time>
            </div>
          ))}
          {records.length === 0 ? <EmptyState label="No studies uploaded yet." /> : null}
        </div>
      </section>
    </div>
  );
}

function UploadFileField({ label, name, accept, icon: Icon = FileText }) {
  const [filename, setFilename] = useState("No file selected");

  return (
    <label className="field upload-field">
      <span>{label}</span>
      <span className="upload-card">
        <span className="upload-card-icon">
          <Icon size={20} />
        </span>
        <span className="upload-card-copy">
          <strong>Choose file</strong>
          <span>{filename}</span>
        </span>
        <input
          type="file"
          name={name}
          accept={accept}
          onChange={(event) => setFilename(event.target.files[0]?.name || "No file selected")}
        />
      </span>
    </label>
  );
}

function CtUploadField({ label, zipRef, folderRef, value, onChange }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="field upload-field ct-unified-field">
      <span>{label}</span>
      <button className="upload-card ct-upload-card" type="button" onClick={() => setOpen((current) => !current)}>
        <span className="upload-card-icon">
          <FolderOpen size={20} />
        </span>
        <span className="upload-card-copy">
          <strong>Upload study</strong>
          <span>{value}</span>
        </span>
      </button>
      <div className={`format-popover ${open ? "" : "is-hidden"}`}>
          <label className="format-option">
            <FileArchive size={18} />
            <span>
              <strong>ZIP file</strong>
              <small>Compressed CT study</small>
            </span>
            <input ref={zipRef} type="file" name="studyZip" accept=".zip,application/zip" onChange={(event) => onChange("zip", event, () => setOpen(false))} />
          </label>
          <label className="format-option">
            <FolderOpen size={18} />
            <span>
              <strong>Folder</strong>
              <small>DICOM folder upload</small>
            </span>
            <input ref={folderRef} type="file" name="studyFolder" webkitdirectory="" directory="" multiple onChange={(event) => onChange("folder", event, () => setOpen(false))} />
          </label>
      </div>
    </div>
  );
}

function appendFilesWithRelativePaths(formData, input) {
  Array.from(input.files || []).forEach((file) => {
    const relativePath = file.webkitRelativePath || file.name;
    formData.append(input.name, file, relativePath);
  });
}

async function submitUploadForm(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const formData = new FormData();

  Array.from(form.elements).forEach((element) => {
    if (!element.name || element.disabled) return;

    if (element.type === "file") {
      if (element.name === "studyFolder") {
        appendFilesWithRelativePaths(formData, element);
        return;
      }

      Array.from(element.files || []).forEach((file) => formData.append(element.name, file, file.name));
      return;
    }

    if ((element.type === "checkbox" || element.type === "radio") && !element.checked) return;
    formData.append(element.name, element.value);
  });

  const response = await fetch(form.action, {
    method: form.method || "post",
    body: formData,
    credentials: "same-origin",
  });

  window.location.assign(response.url || "/dashboard");
}

function UploadView() {
  const [ctLabel, setCtLabel] = useState("No CT study selected");
  const zipRef = useRef(null);
  const folderRef = useRef(null);

  const handleCtChange = (type, event, closeChooser) => {
    const files = event.target.files;
    if (!files.length) return;

    if (type === "zip" && folderRef.current) folderRef.current.value = "";
    if (type === "folder" && zipRef.current) zipRef.current.value = "";

    setCtLabel(type === "folder" ? `${files.length} folder files selected` : files[0].name);
    closeChooser?.();
  };

  return (
    <section className="surface-panel upload-panel">
      <div className="section-head">
        <div>
          <p className="eyebrow">Upload study</p>
          <h2>Anonymize imaging data</h2>
        </div>
      </div>

      <form className="upload-form" method="post" action="/upload" encType="multipart/form-data" onSubmit={submitUploadForm}>
        <UploadFileField label="X-ray / single DICOM" name="dicom" accept=".dcm,application/dicom" icon={FileArchive} />

        <CtUploadField label="CT study" zipRef={zipRef} folderRef={folderRef} value={ctLabel} onChange={handleCtChange} />

        <UploadFileField label="Hematology report" name="histologyReport" accept=".pdf,.doc,.docx,.txt,.rtf,image/*" />

        <UploadFileField label="Radiology report" name="radiologyReport" accept=".pdf,.doc,.docx,.txt,.rtf,image/*" />

        <button className="primary-button submit-upload" type="submit">
          <UploadCloud size={18} />
          Upload and anonymize
        </button>
      </form>
    </section>
  );
}

function ReportPreview({ report }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(report.previewApiUrl)
      .then((response) => response.json())
      .then(setPreview)
      .finally(() => setLoading(false));
  }, [report.previewApiUrl]);

  if (loading) {
    return <div className="report-preview loading-preview">Loading preview...</div>;
  }

  if (!preview || preview.error || preview.kind === "unsupported") {
    return (
      <div className="report-preview unsupported-preview">
        <FileText size={22} />
        <span>Preview is not available for this report format.</span>
      </div>
    );
  }

  if (preview.kind === "pdf") {
    return <iframe className="report-preview" title={`${report.kind} report`} src={preview.url} />;
  }

  if (preview.kind === "image") {
    return (
      <div className="report-preview image-preview">
        <img src={preview.url} alt={`${report.kind} report`} />
      </div>
    );
  }

  if (preview.kind === "html") {
    return <article className="report-preview document-preview" dangerouslySetInnerHTML={{ __html: preview.html }} />;
  }

  return <pre className="report-preview text-preview">{preview.text}</pre>;
}

function DicomViewer({ study }) {
  const [imaging, setImaging] = useState(null);
  const [activeSlice, setActiveSlice] = useState(0);
  const [metadata, setMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const currentSlice = imaging?.slices?.[activeSlice];

  useEffect(() => {
    setLoading(true);
    setImaging(null);
    setActiveSlice(0);
    fetch(`/api/studies/${study.id}/imaging`)
      .then((response) => response.json())
      .then(setImaging)
      .finally(() => setLoading(false));
  }, [study.id]);

  useEffect(() => {
    if (!currentSlice) {
      setMetadata(null);
      return;
    }

    let cancelled = false;
    setMetadataLoading(true);
    setMetadata(null);
    fetch(`/api/studies/${study.id}/imaging/${currentSlice.index}/metadata`)
      .then((response) => response.json())
      .then((payload) => {
        if (!cancelled) setMetadata(payload);
      })
      .catch(() => {
        if (!cancelled) setMetadata({ error: "Unable to load DICOM metadata." });
      })
      .finally(() => {
        if (!cancelled) setMetadataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentSlice, study.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!imaging?.slices?.length) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveSlice((current) => Math.max(0, current - 1));
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveSlice((current) => Math.min(imaging.slices.length - 1, current + 1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [imaging]);

  if (loading) {
    return <div className="dicom-stage loading-preview">Loading image...</div>;
  }

  if (!currentSlice) {
    return (
      <div className="dicom-stage unsupported-preview">
        <FileArchive size={22} />
        <span>No DICOM image is available for this study.</span>
      </div>
    );
  }

  const previousDisabled = activeSlice === 0;
  const nextDisabled = activeSlice === imaging.slices.length - 1;

  return (
    <div className="dicom-viewer">
      <div className="viewer-toolbar">
        <strong>{currentSlice.filename}</strong>
        <span>
          {activeSlice + 1} / {imaging.slices.length}
        </span>
      </div>
      <div className="dicom-layout">
        <div className="dicom-stage">
          <img src={currentSlice.imageUrl} alt={`${study.recordType} ${currentSlice.label}`} />
        </div>
        <aside className="metadata-panel">
          <div className="metadata-head">
            <strong>Metadata</strong>
            <span>{metadata?.tags?.length || 0} tags</span>
          </div>
          {metadataLoading ? <div className="metadata-state">Loading tags...</div> : null}
          {!metadataLoading && metadata?.error ? <div className="metadata-state">{metadata.error}</div> : null}
          {!metadataLoading && metadata?.tags?.length ? (
            <div className="metadata-table">
              {metadata.tags.map((tag) => (
                <div className="metadata-row" key={`${tag.tag}-${tag.keyword || tag.name}`}>
                  <span>{tag.name}</span>
                  <strong>{tag.value}</strong>
                  <small>{tag.tag} {tag.vr}</small>
                </div>
              ))}
            </div>
          ) : null}
        </aside>
      </div>
      <div className="slice-controls">
        <button className="icon-button" type="button" onClick={() => setActiveSlice((current) => Math.max(0, current - 1))} disabled={previousDisabled} aria-label="Previous slice">
          <ChevronLeft size={18} />
        </button>
        <input
          type="range"
          min="0"
          max={Math.max(imaging.slices.length - 1, 0)}
          value={activeSlice}
          onChange={(event) => setActiveSlice(Number(event.target.value))}
          aria-label="CT slice"
        />
        <button className="icon-button" type="button" onClick={() => setActiveSlice((current) => Math.min(imaging.slices.length - 1, current + 1))} disabled={nextDisabled} aria-label="Next slice">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

function StudyViewerModal({ study, initialView = "imaging", initialReport = 0, onClose }) {
  const [view, setView] = useState(initialView);
  const [activeReport, setActiveReport] = useState(initialReport);
  const activeReportData = study.reports?.[activeReport];

  useEffect(() => {
    setView(initialView);
    setActiveReport(initialReport);
  }, [initialReport, initialView, study.id]);

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="modal viewer-modal" role="dialog" aria-modal="true" aria-label="Study viewer" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <p className="eyebrow">Viewer</p>
            <h3>{study.originalFilename}</h3>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="viewer-tabs">
          <button className={view === "imaging" ? "active" : ""} type="button" onClick={() => setView("imaging")}>
            <FileArchive size={16} />
            Imaging
          </button>
          <button className={view === "reports" ? "active" : ""} type="button" onClick={() => setView("reports")} disabled={!study.reports?.length}>
            <FileText size={16} />
            Reports
          </button>
        </div>

        {view === "imaging" ? <DicomViewer study={study} /> : null}
        {view === "reports" && study.reports?.length ? (
          <div className="viewer-report">
            <div className="report-tabs">
              {study.reports.map((report, index) => (
                <button className={activeReport === index ? "active" : ""} type="button" key={report.index} onClick={() => setActiveReport(index)}>
                  <FileText size={16} />
                  {report.kind}
                </button>
              ))}
            </div>
            <div className="report-frame">
              <div className="report-toolbar">
                <strong>{activeReportData.filename}</strong>
                <span>{activeReportData.previewKind.toUpperCase()}</span>
              </div>
              <ReportPreview report={activeReportData} />
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

function StudyDownloadModal({ study, onClose }) {
  const [selected, setSelected] = useState(() => new Set(["imaging"]));

  const toggleItem = (item) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(["imaging", ...study.reports.map((report) => `report-${report.index}`)]));
  };

  const clearReports = () => {
    setSelected(new Set(["imaging"]));
  };

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="modal download-modal" method="get" action={`/download/${study.id}`} onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <p className="eyebrow">Download package</p>
            <h3>{study.originalFilename}</h3>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="download-quick-actions">
          <button className="ghost-button compact" type="button" onClick={selectAll}>
            All in one ZIP
          </button>
          <button className="ghost-button compact" type="button" onClick={clearReports}>
            Imaging only
          </button>
        </div>

        <label className="download-option">
          <input type="checkbox" name="include" value="imaging" checked={selected.has("imaging")} onChange={() => toggleItem("imaging")} />
          <span>
            <strong>{study.recordType === "CT Study" ? "CT / X-ray imaging" : "X-ray imaging"}</strong>
            <small>{study.fileCount} stored item{study.fileCount === 1 ? "" : "s"}</small>
          </span>
        </label>

        {study.reports.map((report) => (
          <label className="download-option" key={report.index}>
            <input
              type="checkbox"
              name="include"
              value={`report-${report.index}`}
              checked={selected.has(`report-${report.index}`)}
              onChange={() => toggleItem(`report-${report.index}`)}
            />
            <span>
              <strong>{report.kind} report</strong>
              <small>{report.filename}</small>
            </span>
          </label>
        ))}

        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={selected.size === 0}>
            <Download size={17} />
            Download selected
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

function StudyProfile({ studyId, onBack }) {
  const [study, setStudy] = useState(null);
  const [activeReport, setActiveReport] = useState(0);
  const [ctLabel, setCtLabel] = useState("No CT study selected");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(null);
  const addZipRef = useRef(null);
  const addFolderRef = useRef(null);
  const activeReportData = study?.reports?.[activeReport];

  const handleAdditionalCtChange = (type, event, closeChooser) => {
    const files = event.target.files;
    if (!files.length) return;

    if (type === "zip" && addFolderRef.current) addFolderRef.current.value = "";
    if (type === "folder" && addZipRef.current) addZipRef.current.value = "";

    setCtLabel(type === "folder" ? `${files.length} folder files selected` : files[0].name);
    closeChooser?.();
  };

  useEffect(() => {
    setStudy(null);
    fetch(`/api/studies/${studyId}`)
      .then((response) => response.json())
      .then((payload) => {
        setStudy(payload);
        setActiveReport(0);
      });
  }, [studyId]);

  if (!study) {
    return <div className="surface-panel loading-screen">Loading study profile...</div>;
  }

  return (
    <div className="profile-grid">
      <section className="surface-panel profile-hero">
        <button className="ghost-button back-button" type="button" onClick={onBack}>
          <ArrowLeft size={17} />
          Back to studies
        </button>
        <div>
          <p className="eyebrow">Study profile</p>
          <h2>{study.originalFilename}</h2>
        </div>
        <div className="profile-meta">
          <span className="type-chip">{study.recordType}</span>
          <span className="storage-chip">
            <Cloud size={14} />
            {study.storageType.toUpperCase()}
          </span>
          <button className="primary-button compact" type="button" onClick={() => setDownloadOpen(true)}>
            <Download size={16} />
            Download
          </button>
        </div>
      </section>

      {downloadOpen ? <StudyDownloadModal study={study} onClose={() => setDownloadOpen(false)} /> : null}
      {viewerOpen ? <StudyViewerModal study={study} initialView={viewerOpen.view} initialReport={viewerOpen.reportIndex || 0} onClose={() => setViewerOpen(null)} /> : null}

      <section className="surface-panel detail-panel">
        <div className="section-head">
          <div>
            <p className="eyebrow">Identifiers</p>
            <h3>Patient and study details</h3>
          </div>
        </div>
        <dl className="detail-list">
          <div>
            <dt>Anonymized patient ID</dt>
            <dd className="mono">{study.anonymizedPatientId}</dd>
          </div>
          <div>
            <dt>Uploaded</dt>
            <dd>{formatDate(study.createdAt)}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{formatDate(study.updatedAt || study.createdAt)}</dd>
          </div>
          <div>
            <dt>Uploaded by</dt>
            <dd>{study.uploadedBy || "Unknown"}</dd>
          </div>
          <div>
            <dt>Items stored</dt>
            <dd>{study.fileCount}</dd>
          </div>
          <div>
            <dt>CT additions</dt>
            <dd>{study.ctAdditions.length}</dd>
          </div>
          <div>
            <dt>Download name</dt>
            <dd>{study.downloadName}</dd>
          </div>
        </dl>
      </section>

      <section className="surface-panel uploaded-data-panel">
        <div className="section-head">
          <div>
            <p className="eyebrow">Patient profile</p>
            <h3>Uploaded data</h3>
          </div>
        </div>
        <div className="table-shell compact-table">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Type</th>
                <th>Items</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="filename-cell">{study.originalFilename}</td>
                <td><span className="type-chip">{study.recordType}</span></td>
                <td>{study.fileCount}</td>
                <td>{formatDate(study.updatedAt || study.createdAt)}</td>
                <td>
                  <button className="icon-button" type="button" onClick={() => setViewerOpen({ view: "imaging" })} aria-label="View imaging">
                    <Eye size={17} />
                  </button>
                </td>
              </tr>
              {study.ctAdditions.map((addition, index) => (
                <tr key={`${addition.filename}-${index}`}>
                  <td className="filename-cell">{addition.filename}</td>
                  <td><span className="type-chip">CT addition</span></td>
                  <td>{addition.fileCount}</td>
                  <td>{formatDate(addition.addedAt || study.updatedAt || study.createdAt)}</td>
                  <td>
                    <button className="icon-button" type="button" onClick={() => setViewerOpen({ view: "imaging" })} aria-label="View imaging">
                      <Eye size={17} />
                    </button>
                  </td>
                </tr>
              ))}
              {study.reports.map((report) => (
                <tr key={report.index}>
                  <td className="filename-cell">{report.filename}</td>
                  <td><span className="type-chip">{report.kind} report</span></td>
                  <td>1</td>
                  <td>{formatDate(study.createdAt)}</td>
                  <td>
                    <button className="icon-button" type="button" onClick={() => setViewerOpen({ view: "reports", reportIndex: report.index })} aria-label={`View ${report.kind} report`}>
                      <Eye size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="surface-panel reports-panel">
        <div className="section-head">
          <div>
            <p className="eyebrow">Reports</p>
            <h3>Attached clinical reports</h3>
          </div>
        </div>

        {study.reports.length ? (
          <>
            <div className="report-tabs">
              {study.reports.map((report, index) => (
                <button className={activeReport === index ? "active" : ""} type="button" key={report.index} onClick={() => setActiveReport(index)}>
                  <FileText size={16} />
                  {report.kind}
                </button>
              ))}
            </div>
            <div className="report-frame">
              <div className="report-toolbar">
                <strong>{activeReportData.filename}</strong>
                <span>{activeReportData.previewKind.toUpperCase()}</span>
              </div>
              <ReportPreview report={activeReportData} />
            </div>
          </>
        ) : (
          <EmptyState label="No hematology or radiology reports were uploaded for this study." />
        )}
      </section>

      <section className="surface-panel add-info-panel">
        <div className="section-head">
          <div>
            <p className="eyebrow">Add information</p>
            <h3>Upload more reports or CT imaging later</h3>
          </div>
        </div>
        <div className="add-info-grid">
          <form className="add-info-form" method="post" action={`/studies/${study.id}/reports`} encType="multipart/form-data">
            <UploadFileField label="Hematology report" name="histologyReport" accept=".pdf,.doc,.docx,.txt,.rtf,image/*" />
            <UploadFileField label="Radiology report" name="radiologyReport" accept=".pdf,.doc,.docx,.txt,.rtf,image/*" />
            <button className="primary-button submit-upload" type="submit">
              <UploadCloud size={18} />
              Add reports
            </button>
          </form>

          <form className="add-info-form ct-add-form" method="post" action={`/studies/${study.id}/ct`} encType="multipart/form-data" onSubmit={submitUploadForm}>
            <CtUploadField label="Additional CT study" zipRef={addZipRef} folderRef={addFolderRef} value={ctLabel} onChange={handleAdditionalCtChange} />
            <button className="primary-button submit-upload" type="submit">
              <UploadCloud size={18} />
              Add CT study
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function StudiesView({ records, setStudyId, searchTerm, setSearchTerm }) {
  return (
    <section className="surface-panel">
      <div className="section-head studies-head">
        <div>
          <p className="eyebrow">Studies</p>
          <h2>Uploaded studies</h2>
        </div>
        <label className="search-box">
          <Search size={17} />
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by file, anonymized ID, or type"
          />
        </label>
      </div>

      <div className="table-shell">
        <table>
          <thead>
            <tr>
              <th>Updated</th>
              <th>Type</th>
              <th>Anonymized ID</th>
              <th>File</th>
              <th>Items</th>
              <th>Reports</th>
              <th>Storage</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id}>
                <td>{formatDate(record.updatedAt || record.createdAt)}</td>
                <td><span className="type-chip">{record.recordType}</span></td>
                <td><span className="mono">{record.anonymizedPatientId}</span></td>
                <td className="filename-cell">
                  {record.originalFilename}
                  {record.ctAdditions?.length ? (
                    <span className="subtle-line">Latest: {record.ctAdditions.at(-1).filename}</span>
                  ) : null}
                </td>
                <td>{record.fileCount}</td>
                <td>
                  <div className="report-list">
                    {record.reports.length ? (
                      record.reports.map((report) => (
                        <a key={report.index} href={`/report/${record.id}/${report.index}`}>
                          {report.kind}
                        </a>
                      ))
                    ) : (
                      <span className="muted">None</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="storage-chip">
                    <Cloud size={14} />
                    {record.storageType.toUpperCase()}
                  </span>
                </td>
                <td>
                  <button className="primary-button compact" type="button" onClick={() => setStudyId(record.id)}>
                    <FileSearch size={16} />
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {records.length === 0 ? <EmptyState label="No studies match this view." /> : null}
      </div>

    </section>
  );
}

function EmptyState({ label }) {
  return (
    <div className="empty-state">
      <Archive size={24} />
      <span>{label}</span>
    </div>
  );
}

function DashboardApp() {
  const [query, updateQuery] = useQuery();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const activeTab = query.get("tab") || "dashboard";
  const studyId = query.get("study") || "";
  const searchTerm = query.get("search") || "";
  const success = query.get("success");
  const error = query.get("error");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    fetch(`/api/dashboard${params.toString() ? `?${params.toString()}` : ""}`)
      .then((response) => {
        if (response.status === 401) {
          window.location.href = "/login";
          return null;
        }
        if (!response.ok) {
          throw new Error("Unable to reach the Dectrocel backend service.");
        }
        return response.json();
      })
      .then((payload) => {
        if (payload) setData(payload);
      })
      .catch((error) => {
        setLoadError(error.message);
      })
      .finally(() => setLoading(false));
  }, [searchTerm]);

  const setTab = (tab) => updateQuery({ tab, study: null, search: tab === "studies" ? searchTerm : null });
  const setStudyId = (id) => updateQuery({ study: id, tab: "studies" });
  const setSearchTerm = (search) => updateQuery({ search, tab: "studies", study: null });

  if (loading && !data) {
    return <div className="loading-screen">Loading workspace...</div>;
  }

  if (!data) {
    return (
      <div className="loading-screen app-error">
        <strong>Dectrocel Data Portal backend is not responding.</strong>
        <span>{loadError || "Start the Node service or configure your web server to reverse proxy to localhost:3000."}</span>
      </div>
    );
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="product-mark">
          <div className="brand-mark compact-mark">
            <img src="/dectrocel-logo.svg" alt="" aria-hidden="true" />
          </div>
          <div>
            <strong>Dectrocel</strong>
            <span>{data.storageMode.toUpperCase()} storage</span>
          </div>
        </div>

        <nav className="side-nav" aria-label="Primary">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button className={activeTab === tab.id ? "active" : ""} type="button" key={tab.id} onClick={() => setTab(tab.id)}>
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        <form className="logout-form" method="post" action="/logout">
          <button className="secondary-button" type="submit">
            <LogOut size={17} />
            Logout
          </button>
        </form>
      </aside>

      <section className="workspace">
        <header className="workspace-head">
          <div>
            <p className="eyebrow">Dectrocel Data Portal</p>
            <h1>{activeTab === "upload" ? "Upload study" : activeTab === "studies" ? "Study archive" : "Dashboard"}</h1>
          </div>
          <div className="user-pill">
            <ShieldCheck size={16} />
            {data.user.username}
          </div>
        </header>

        {error ? <div className="alert error">{error}</div> : null}
        {success ? <div className="alert success">{success}</div> : null}

        {activeTab === "upload" ? <UploadView /> : null}
        {activeTab === "studies" && studyId ? <StudyProfile studyId={studyId} onBack={() => setStudyId(null)} /> : null}
        {activeTab === "studies" && !studyId ? (
            <StudiesView
              records={data.records}
              setStudyId={setStudyId}
              searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        ) : null}
        {activeTab === "dashboard" ? <DashboardHome records={data.records} stats={data.stats} setTab={setTab} /> : null}
      </section>
    </main>
  );
}

function AuthenticatedApp() {
  const [identity,setIdentity]=useState(null);
  useEffect(()=>{fetch("/api/v2/me",{credentials:"same-origin"}).then(async response=>{if(response.status===401){window.location.replace("/login");return null;}if(!response.ok)throw new Error("Unable to load account workspace.");return response.json();}).then(body=>{if(body)setIdentity(body);}).catch(error=>setIdentity({error:error.message}));},[]);
  if(!identity)return <div className="app-bootstrap">Loading secure workspace…</div>;
  if(identity.error)throw new Error(identity.error);
  return <PlatformApp/>;
}

function App() {
  if (window.location.pathname.startsWith("/login")) {
    return <LoginPage />;
  }
  return <AuthenticatedApp />;
}

class PortalErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("Portal render failure", error, info);fetch("/api/client-errors",{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json"},body:JSON.stringify({message:error?.message,componentStack:info?.componentStack,path:window.location.pathname})}).catch(()=>{}); }
  render() {
    if (!this.state.error) return this.props.children;
    return <main className="fatal-recovery"><section><h1>Unable to display the workspace</h1><p>The browser loaded an incompatible or damaged application state.</p><code>{this.state.error?.message||"Unknown render error"}</code><pre>{String(this.state.error?.stack||"").split("\n").slice(0,4).join("\n")}</pre><button onClick={()=>{localStorage.clear();sessionStorage.clear();window.location.replace(`/dashboard?recovery=${Date.now()}`);}}>Reload cleanly</button><small>The technical error has been reported securely to Dectrocel support.</small></section></main>;
  }
}

createRoot(document.getElementById("root")).render(
  <PortalErrorBoundary>
    <App />
  </PortalErrorBoundary>
);
